// Composition form on the spool card. Workshop staff record:
//   * the spool's default pipe size (pre-populates new rows);
//   * per (operation, pipe size, count) for cut / roll-groove /
//     thread — modelled as one row per size so a spool that
//     reduces from DN50 to DN25 along its length carries two
//     cutting rows;
//   * per (weld type, pipe size, count) for every weld joint.
//
// Both lists share the same UX: a row-with-three-selects
// (operation / size / count) + Add. The server re-runs the
// cycle-times extrapolator on every save; onAfterSave is the cue
// for the parent to refetch the job so the pipeline strip + Gantt
// bars refresh with the new hours.
import { useEffect, useMemo, useState } from "react";
import type {
  PipeSizeDto,
  SpoolCompositionDto,
  WeldTypeDto,
} from "../../../../../../shared/types/production-schedule";
import {
  getSpoolComposition,
  upsertSpoolComposition,
  useCycleTimes,
} from "../hooks/useProductionApi";

interface Props {
  jobId: number;
  spoolId: number;
  onAfterSave?: () => void | Promise<void>;
}

type StageRow = { stageKey: string; pipeSizeId: number; count: number };
type WeldRow = { weldTypeId: number; pipeSizeId: number; count: number };

// The three per-size pipe operations the spool composition tracks.
// Keep in sync with PER_SIZE_STAGES in extrapolateStageHours.ts.
const STAGE_OPS: { key: string; label: string }[] = [
  { key: "cutting", label: "Cut" },
  { key: "rollgroove", label: "Roll groove" },
  { key: "threading", label: "Thread" },
];

export function SpoolComposition({ jobId, spoolId, onAfterSave }: Props) {
  const { data: catalogue } = useCycleTimes();
  const [pipeSizeId, setPipeSizeId] = useState<number | null>(null);
  const [stages, setStages] = useState<StageRow[]>([]);
  const [welds, setWelds] = useState<WeldRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const c = await getSpoolComposition(jobId, spoolId);
        if (cancelled) return;
        if (c) {
          setPipeSizeId(c.pipeSizeId);
          setStages(c.stageCounts);
          setWelds(c.weldCounts);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [jobId, spoolId]);

  const activePipeSizes = useMemo(
    () => (catalogue?.pipeSizes ?? []).filter((p) => !p.archived).sort((a, b) => a.nbMm - b.nbMm),
    [catalogue],
  );
  const activeWeldTypes = useMemo(
    () => (catalogue?.weldTypes ?? []).filter((t) => !t.archived).sort((a, b) => a.sortOrder - b.sortOrder),
    [catalogue],
  );
  const pipeById = useMemo(
    () => new Map((catalogue?.pipeSizes ?? []).map((p) => [p.id, p])),
    [catalogue],
  );
  const weldById = useMemo(
    () => new Map((catalogue?.weldTypes ?? []).map((t) => [t.id, t])),
    [catalogue],
  );

  const save = async (next: {
    pipeSizeId: number | null;
    stageCounts: StageRow[];
    weldCounts: WeldRow[];
  }) => {
    setBusy(true);
    setError(null);
    try {
      await upsertSpoolComposition(jobId, spoolId, {
        pipeSizeId: next.pipeSizeId,
        stageCounts: next.stageCounts.filter((c) => c.count > 0),
        weldCounts: next.weldCounts.filter((c) => c.count > 0),
      });
      await onAfterSave?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const current = () => ({
    pipeSizeId,
    stageCounts: stages,
    weldCounts: welds,
  });

  const setPipeAndSave = (id: number | null) => {
    setPipeSizeId(id);
    void save({ ...current(), pipeSizeId: id });
  };
  const setStagesAndSave = (next: StageRow[]) => {
    setStages(next);
    void save({ ...current(), stageCounts: next });
  };
  const setWeldsAndSave = (next: WeldRow[]) => {
    setWelds(next);
    void save({ ...current(), weldCounts: next });
  };

  if (!loaded) {
    return (
      <div style={{ padding: 10, fontSize: 11, color: "var(--bsp-fg-muted)" }}>
        Loading composition…
      </div>
    );
  }
  if (!catalogue) {
    return (
      <div style={{ padding: 10, fontSize: 11, color: "var(--bsp-fg-muted)" }}>
        Cycle times catalogue isn't loaded. Open Settings → Cycle times to record some values first.
      </div>
    );
  }

  const defaultPipeForNewRow = pipeSizeId ?? activePipeSizes[0]?.id ?? null;

  return (
    <div style={{
      padding: 10,
      background: "var(--bsp-surface-muted)",
      border: "1px solid var(--bsp-border)",
      borderRadius: 4,
      marginBottom: 8,
    }}>
      <div style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 12,
        marginBottom: 10,
      }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: "var(--bsp-fg-muted)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
          Composition
        </span>
        <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11 }}>
          Default pipe size
          <select
            value={pipeSizeId ?? ""}
            disabled={busy}
            onChange={(e) => setPipeAndSave(e.target.value === "" ? null : Number(e.target.value))}
            style={{ padding: "2px 6px", fontFamily: "var(--bsp-font-mono)" }}
            title="Pre-populates new operation + weld rows. Each row can still be edited to a different size."
          >
            <option value="">—</option>
            {activePipeSizes.map((p) => (
              <option key={p.id} value={p.id}>{p.label}</option>
            ))}
          </select>
        </label>
      </div>
      <PipeOpsEditor
        rows={stages}
        activePipeSizes={activePipeSizes}
        pipeById={pipeById}
        disabled={busy}
        defaultPipeSizeId={defaultPipeForNewRow}
        onChange={setStagesAndSave}
      />
      <WeldsEditor
        rows={welds}
        activeWeldTypes={activeWeldTypes}
        activePipeSizes={activePipeSizes}
        weldById={weldById}
        pipeById={pipeById}
        disabled={busy}
        defaultPipeSizeId={defaultPipeForNewRow}
        onChange={setWeldsAndSave}
      />
      {error ? (
        <div style={{ color: "var(--bsp-red-fg)", fontSize: 11, marginTop: 6 }}>{error}</div>
      ) : null}
    </div>
  );
}

function PipeOpsEditor({
  rows,
  activePipeSizes,
  pipeById,
  disabled,
  defaultPipeSizeId,
  onChange,
}: {
  rows: StageRow[];
  activePipeSizes: PipeSizeDto[];
  pipeById: Map<number, PipeSizeDto>;
  disabled: boolean;
  defaultPipeSizeId: number | null;
  onChange: (next: StageRow[]) => void;
}) {
  const updateAt = (idx: number, patch: Partial<StageRow>) => {
    onChange(rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };
  const removeAt = (idx: number) => onChange(rows.filter((_, i) => i !== idx));
  // Pick the first (stageKey, pipeSizeId) pair that isn't already
  // recorded — pressing "Add" twice in a row otherwise duplicates
  // the row, and the migration's UNIQUE (spool_id, stage_key,
  // pipe_size_id) rejects the autosave. The button disables when
  // every combination is taken.
  const takenKey = (stageKey: string, pipeSizeId: number) =>
    `${stageKey}:${pipeSizeId}`;
  const taken = new Set(rows.map((r) => takenKey(r.stageKey, r.pipeSizeId)));
  const firstFree: { stageKey: string; pipeSizeId: number } | null = (() => {
    const pipes = activePipeSizes.length > 0 ? activePipeSizes : (defaultPipeSizeId ? [{ id: defaultPipeSizeId } as PipeSizeDto] : []);
    // Prefer the spool's default pipe size; sweep stage ops first
    // there before falling through to other sizes.
    const sortedPipes = defaultPipeSizeId
      ? [...pipes].sort((a, b) => (a.id === defaultPipeSizeId ? -1 : b.id === defaultPipeSizeId ? 1 : 0))
      : pipes;
    for (const op of STAGE_OPS) {
      for (const p of sortedPipes) {
        if (!taken.has(takenKey(op.key, p.id))) return { stageKey: op.key, pipeSizeId: p.id };
      }
    }
    return null;
  })();
  const addRow = () => {
    if (!firstFree) return;
    onChange([...rows, { stageKey: firstFree.stageKey, pipeSizeId: firstFree.pipeSizeId, count: 1 }]);
  };
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: "var(--bsp-fg-muted)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>
        Pipe operations
      </div>
      {rows.length === 0 ? (
        <div style={{ fontSize: 11, color: "var(--bsp-fg-muted)", marginBottom: 4 }}>
          No cuts / grooves / threads recorded.
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "auto auto 80px 30px", gap: 4, alignItems: "center", marginBottom: 4 }}>
          {rows.map((r, i) => (
            <PipeOpRow
              key={`${i}:${r.stageKey}:${r.pipeSizeId}`}
              row={r}
              idx={i}
              activePipeSizes={activePipeSizes}
              pipeById={pipeById}
              disabled={disabled}
              onChange={(patch) => updateAt(i, patch)}
              onRemove={() => removeAt(i)}
            />
          ))}
        </div>
      )}
      <button
        type="button"
        className="btn-sm"
        onClick={addRow}
        disabled={disabled || activePipeSizes.length === 0 || firstFree === null}
        title={firstFree === null
          ? "Every cut / groove / thread × size combination is already recorded — edit a row instead"
          : undefined}
      >
        + Add cut / groove / thread
      </button>
    </div>
  );
}

function PipeOpRow({
  row,
  idx,
  activePipeSizes,
  pipeById,
  disabled,
  onChange,
  onRemove,
}: {
  row: StageRow;
  idx: number;
  activePipeSizes: PipeSizeDto[];
  pipeById: Map<number, PipeSizeDto>;
  disabled: boolean;
  onChange: (patch: Partial<StageRow>) => void;
  onRemove: () => void;
}) {
  const sizeRow = pipeById.get(row.pipeSizeId);
  const [count, setCount] = useState(String(row.count));
  useEffect(() => { setCount(String(row.count)); }, [row.count]);
  return (
    <>
      <select
        value={row.stageKey}
        disabled={disabled}
        onChange={(e) => onChange({ stageKey: e.target.value })}
        style={{ padding: "2px 6px", fontSize: 11 }}
        aria-label={`Pipe op ${idx + 1} type`}
      >
        {STAGE_OPS.find((s) => s.key === row.stageKey) === undefined ? (
          <option value={row.stageKey}>{row.stageKey} (unknown)</option>
        ) : null}
        {STAGE_OPS.map((s) => (
          <option key={s.key} value={s.key}>{s.label}</option>
        ))}
      </select>
      <select
        value={row.pipeSizeId}
        disabled={disabled}
        onChange={(e) => onChange({ pipeSizeId: Number(e.target.value) })}
        style={{ padding: "2px 6px", fontFamily: "var(--bsp-font-mono)", fontSize: 11 }}
        aria-label={`Pipe op ${idx + 1} size`}
      >
        {!sizeRow || sizeRow.archived ? (
          <option value={row.pipeSizeId}>{sizeRow?.label ?? "?"} (archived)</option>
        ) : null}
        {activePipeSizes.map((p) => (
          <option key={p.id} value={p.id}>{p.label}</option>
        ))}
      </select>
      <input
        type="number"
        min={0}
        step={1}
        value={count}
        disabled={disabled}
        onChange={(e) => setCount(e.target.value)}
        onBlur={() => {
          const n = Math.max(0, Math.floor(Number(count) || 0));
          if (n !== row.count) onChange({ count: n });
        }}
        style={{ padding: "2px 4px", textAlign: "right", fontFamily: "var(--bsp-font-mono)", fontSize: 11 }}
        aria-label={`Pipe op ${idx + 1} count`}
      />
      <button
        type="button"
        className="btn-sm danger"
        disabled={disabled}
        onClick={onRemove}
        title="Remove row"
        aria-label={`Remove pipe op ${idx + 1}`}
        style={{ padding: "2px 6px" }}
      >×</button>
    </>
  );
}

function WeldsEditor({
  rows,
  activeWeldTypes,
  activePipeSizes,
  weldById,
  pipeById,
  disabled,
  defaultPipeSizeId,
  onChange,
}: {
  rows: WeldRow[];
  activeWeldTypes: WeldTypeDto[];
  activePipeSizes: PipeSizeDto[];
  weldById: Map<number, WeldTypeDto>;
  pipeById: Map<number, PipeSizeDto>;
  disabled: boolean;
  defaultPipeSizeId: number | null;
  onChange: (next: WeldRow[]) => void;
}) {
  if (activeWeldTypes.length === 0) return null;
  const updateAt = (idx: number, patch: Partial<WeldRow>) => {
    onChange(rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };
  const removeAt = (idx: number) => onChange(rows.filter((_, i) => i !== idx));
  // Pick the first (weldTypeId, pipeSizeId) pair that isn't already
  // recorded — same UNIQUE-violation guard as the pipe-ops editor.
  const takenKey = (weldTypeId: number, pipeSizeId: number) => `${weldTypeId}:${pipeSizeId}`;
  const taken = new Set(rows.map((r) => takenKey(r.weldTypeId, r.pipeSizeId)));
  const firstFree: { weldTypeId: number; pipeSizeId: number } | null = (() => {
    const pipes = activePipeSizes.length > 0
      ? (defaultPipeSizeId
          ? [...activePipeSizes].sort((a, b) =>
              a.id === defaultPipeSizeId ? -1 : b.id === defaultPipeSizeId ? 1 : 0)
          : activePipeSizes)
      : (defaultPipeSizeId ? [{ id: defaultPipeSizeId } as PipeSizeDto] : []);
    for (const t of activeWeldTypes) {
      for (const p of pipes) {
        if (!taken.has(takenKey(t.id, p.id))) return { weldTypeId: t.id, pipeSizeId: p.id };
      }
    }
    return null;
  })();
  const addRow = () => {
    if (!firstFree) return;
    onChange([...rows, { weldTypeId: firstFree.weldTypeId, pipeSizeId: firstFree.pipeSizeId, count: 1 }]);
  };
  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 700, color: "var(--bsp-fg-muted)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>
        Weld joints
      </div>
      {rows.length === 0 ? (
        <div style={{ fontSize: 11, color: "var(--bsp-fg-muted)", marginBottom: 4 }}>
          No welds recorded.
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "auto auto 80px 30px", gap: 4, alignItems: "center", marginBottom: 4 }}>
          {rows.map((r, i) => (
            <WeldRow
              key={`${i}:${r.weldTypeId}:${r.pipeSizeId}`}
              row={r}
              idx={i}
              activeWeldTypes={activeWeldTypes}
              activePipeSizes={activePipeSizes}
              weldById={weldById}
              pipeById={pipeById}
              disabled={disabled}
              onChange={(patch) => updateAt(i, patch)}
              onRemove={() => removeAt(i)}
            />
          ))}
        </div>
      )}
      <button
        type="button"
        className="btn-sm"
        onClick={addRow}
        disabled={disabled || activeWeldTypes.length === 0 || firstFree === null}
        title={firstFree === null
          ? "Every weld type × size combination is already recorded — edit a row instead"
          : undefined}
      >
        + Add weld
      </button>
    </div>
  );
}

function WeldRow({
  row,
  idx,
  activeWeldTypes,
  activePipeSizes,
  weldById,
  pipeById,
  disabled,
  onChange,
  onRemove,
}: {
  row: WeldRow;
  idx: number;
  activeWeldTypes: WeldTypeDto[];
  activePipeSizes: PipeSizeDto[];
  weldById: Map<number, WeldTypeDto>;
  pipeById: Map<number, PipeSizeDto>;
  disabled: boolean;
  onChange: (patch: Partial<WeldRow>) => void;
  onRemove: () => void;
}) {
  const typeRow = weldById.get(row.weldTypeId);
  const sizeRow = pipeById.get(row.pipeSizeId);
  const [count, setCount] = useState(String(row.count));
  useEffect(() => { setCount(String(row.count)); }, [row.count]);
  return (
    <>
      <select
        value={row.weldTypeId}
        disabled={disabled}
        onChange={(e) => onChange({ weldTypeId: Number(e.target.value) })}
        style={{ padding: "2px 6px", fontSize: 11 }}
        aria-label={`Weld ${idx + 1} type`}
      >
        {!typeRow || typeRow.archived ? (
          <option value={row.weldTypeId}>
            {typeRow?.label ?? "(unknown)"} (archived)
          </option>
        ) : null}
        {activeWeldTypes.map((t) => (
          <option key={t.id} value={t.id}>{t.label}</option>
        ))}
      </select>
      <select
        value={row.pipeSizeId}
        disabled={disabled}
        onChange={(e) => onChange({ pipeSizeId: Number(e.target.value) })}
        style={{ padding: "2px 6px", fontFamily: "var(--bsp-font-mono)", fontSize: 11 }}
        aria-label={`Weld ${idx + 1} size`}
      >
        {!sizeRow || sizeRow.archived ? (
          <option value={row.pipeSizeId}>{sizeRow?.label ?? "?"} (archived)</option>
        ) : null}
        {activePipeSizes.map((p) => (
          <option key={p.id} value={p.id}>{p.label}</option>
        ))}
      </select>
      <input
        type="number"
        min={0}
        step={1}
        value={count}
        disabled={disabled}
        onChange={(e) => setCount(e.target.value)}
        onBlur={() => {
          const n = Math.max(0, Math.floor(Number(count) || 0));
          if (n !== row.count) onChange({ count: n });
        }}
        style={{ padding: "2px 4px", textAlign: "right", fontFamily: "var(--bsp-font-mono)", fontSize: 11 }}
        aria-label={`Weld ${idx + 1} count`}
      />
      <button
        type="button"
        className="btn-sm danger"
        disabled={disabled}
        onClick={onRemove}
        title="Remove weld"
        aria-label={`Remove weld ${idx + 1}`}
        style={{ padding: "2px 6px" }}
      >×</button>
    </>
  );
}
